// Helpers shared by the figure editing wizards (graves, size figures).

const POINT_FIELDS = ['controlPoint', 'anchorPoint'].flatMap((prefix) =>
  [1, 2, 3, 4].flatMap((index) => ['X', 'Y'].map((axis) => `${prefix}${index}${axis}`))
);

// Only the fields accepted by the `updateFigure` GraphQL mutation. Figures
// coming from the box resizer carry extra keys (typeName, angle, contour,
// page ids, ...) that would make the mutation fail validation.
export const FIGURE_INPUT_FIELDS = [
  'type',
  'verified',
  'disturbed',
  'siteId',
  'identifier',
  'x1',
  'y1',
  'x2',
  'y2',
  'manualBoundingBox',
  'boundingBoxAngle',
  'boundingBoxWidth',
  'boundingBoxHeight',
  ...POINT_FIELDS
];

// Builds the updateFigure input for a single box-resizer figure.
export function figureUpdateInput(figure) {
  const input = { id: figure.id };
  FIGURE_INPUT_FIELDS.forEach((key) => {
    if (figure[key] !== undefined) input[key] = figure[key];
  });
  return input;
}

export const figureUpdateInputs = (figures = []) => figures.map(figureUpdateInput);

// Drops GraphQL/auxiliary attributes so a figure can be sent to the API.
export function cleanFigure(figure = {}) {
  const { __typename, text, manualWidth, ...rest } = figure;
  return rest;
}

// Page image in the shape the SVG previews and Konva canvases expect.
export function pageImage(resource) {
  const image = resource?.page?.image;
  return image ? { ...image, href: image.url } : null;
}

export function parseIntOrNull(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export const distance = (from, to) => Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);

// List page for a figure type — wizard breadcrumbs, delete redirects and
// "next figure" fallbacks must land on real SPA routes.
const FIGURE_LIST_PATHS = {
  lithics: '/lithics',
  stonetool: '/lithics',
  ceramics: '/ceramics',
  ceramic: '/ceramics',
  grave: '/graves'
};

export const figureListPath = (type) =>
  FIGURE_LIST_PATHS[String(type ?? '').toLowerCase()] ?? '/graves';

// Saves the scale step: edits to the scale figure first, then the scale
// attributes of the resource itself.
export async function saveScale({ data, resourceId, updateScale, updateFigure }) {
  const { id: scaleId, ...figureUpdates } = cleanFigure(data.figure);
  if (scaleId && String(scaleId) !== String(resourceId)) {
    await updateFigure({ variables: { input: { id: scaleId, ...figureUpdates } } });
  }

  const input = { id: resourceId };
  if (data.percentageScale) input.percentageScale = data.percentageScale;
  if (data.pageSize) input.pageSize = parseInt(data.pageSize, 10);
  if (data.scaleText) input.text = data.scaleText;
  await updateScale({ variables: { input } });
}
